// --- src/data/geo.js -------------------------------------------------------
// Unified geographic entity layer -- single source of truth for countries, regions, cities.
// Imports Italy entities from ./italy/. UK/Ireland entities defined inline.
// All lookup helpers fallback safely -- never crash on missing data.

import { ITALY_COUNTRY } from "./italy/country.js";
import { ITALY_REGIONS } from "./italy/regions.js";
import { ITALY_CITIES } from "./italy/cities.js";
import { UAE_COUNTRY } from "./uae/country.js";
import { UAE_REGIONS as UAE_REGIONS_RAW } from "./uae/regions.js";
import { UAE_CITIES } from "./uae/cities.js";
// Fuller catalogue registries, geo.js only inlines a subset of countries/regions,
// so lookups fall back to these so the whole directory resolves (one source of truth).
import { COUNTRY_REGISTRY } from "./countryRegistry.js";
import { DIRECTORY_REGIONS } from "./directoryRegions.js";
import {
  SLUG_TO_LISTING_TYPES,
  VENDOR_CATEGORIES_FROM_REGISTRY,
} from "./categoryRegistry.js";

// ---------------------------------------------------------------------------
// VENDOR CATEGORIES, derived from the category registry
// ---------------------------------------------------------------------------

export const VENDOR_CATEGORIES = VENDOR_CATEGORIES_FROM_REGISTRY.map(c => ({
  slug:  c.slug,
  label: c.label,
  icon:  c.icon,
}));

// ---------------------------------------------------------------------------
// COUNTRIES
// ---------------------------------------------------------------------------

export const COUNTRIES = [
  {
    slug: "england",
    name: "England",
    iso2: "GB",
    listingCount: 6,
    britishIslesSiblings: ["scotland", "wales", "ireland", "northern-ireland"],
    seo: {
      title: "Luxury Wedding Venues in England | Luxury Wedding Directory",
      metaDescription:
        "Discover curated luxury wedding venues and vendors across England. London, the Cotswolds, Lake District and beyond.",
      canonicalPath: "/england",
    },
    ai: {
      summary:
        "Largest domestic wedding market. Strong county-level intent. London, Cotswolds, and Lake District lead search volume.",
      focusKeywords: [
        "england wedding venues",
        "luxury wedding england",
        "wedding venues uk",
      ],
    },
    regions: [
      "london", "surrey", "kent", "sussex", "hampshire", "berkshire",
      "buckinghamshire", "oxfordshire", "essex", "hertfordshire",
      "devon", "cornwall", "somerset", "dorset", "wiltshire",
      "gloucestershire", "suffolk", "norfolk", "cambridgeshire", "bristol",
      "warwickshire", "staffordshire", "cheshire", "manchester",
      "lancashire", "derbyshire", "leicestershire", "nottinghamshire",
      "shropshire", "liverpool",
      "yorkshire", "northumberland", "durham", "cumbria", "lake-district",
    ],
  },
  {
    slug: "scotland",
    name: "Scotland",
    iso2: "GB",
    listingCount: 0,
    britishIslesSiblings: ["england", "wales", "ireland", "northern-ireland"],
    seo: {
      title: "Luxury Wedding Venues in Scotland | Luxury Wedding Directory",
      metaDescription:
        "Discover Scotland's finest luxury wedding venues and vendors. Edinburgh castles, Highland estates and coastal celebrations.",
      canonicalPath: "/scotland",
    },
    ai: {
      summary:
        "Castle and estate wedding market. Edinburgh city and Highland wilderness lead demand. Strong international appeal from US and European diaspora.",
      focusKeywords: [
        "scotland wedding venues",
        "luxury wedding scotland",
        "scottish castle wedding",
        "highland wedding venue",
      ],
    },
    regions: ["edinburgh", "highlands"],
  },
  {
    slug: "wales",
    name: "Wales",
    iso2: "GB",
    listingCount: 0,
    britishIslesSiblings: ["england", "scotland", "ireland", "northern-ireland"],
    seo: {
      title: "Luxury Wedding Venues in Wales | Luxury Wedding Directory",
      metaDescription:
        "Discover Wales's finest luxury wedding venues and vendors. Castle celebrations, coastal estates and Welsh countryside retreats.",
      canonicalPath: "/wales",
    },
    ai: {
      summary:
        "Castle and country estate market. Cardiff and Pembrokeshire coast lead. Growing luxury segment with strong English cross-border demand.",
      focusKeywords: [
        "wales wedding venues",
        "luxury wedding wales",
        "welsh castle wedding",
        "pembrokeshire wedding venue",
      ],
    },
    regions: ["cardiff", "pembrokeshire"],
  },
  {
    slug: "northern-ireland",
    name: "Northern Ireland",
    iso2: "GB",
    listingCount: 0,
    britishIslesSiblings: ["england", "scotland", "wales", "ireland"],
    seo: {
      title: "Luxury Wedding Venues in Northern Ireland | Luxury Wedding Directory",
      metaDescription:
        "Discover Northern Ireland's finest luxury wedding venues. Belfast's grand hotels, coastal castles and Antrim countryside estates.",
      canonicalPath: "/northern-ireland",
    },
    ai: {
      summary:
        "Emerging luxury destination. Belfast urban market and Antrim coast lead. Strong cross-border demand with Republic of Ireland.",
      focusKeywords: [
        "northern ireland wedding venues",
        "luxury wedding belfast",
        "belfast wedding venue",
      ],
    },
    regions: ["belfast"],
  },
  {
    slug: "italy",
    name: "Italy",
    iso2: "IT",
    listingCount: ITALY_COUNTRY.listingCount,
    seo: {
      title: ITALY_COUNTRY.seoTitleTemplate,
      metaDescription: ITALY_COUNTRY.metaDescriptionTemplate,
      canonicalPath: "/italy",
    },
    ai: {
      summary: ITALY_COUNTRY.aiSummary,
      focusKeywords: ITALY_COUNTRY.focusKeywords,
    },
    regions: ITALY_REGIONS.filter(r => !r.isLegacy).map(r => r.slug),
  },
  {
    slug: "france",
    name: "France",
    iso2: "FR",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in France | Luxury Wedding Directory",
      metaDescription:
        "Discover France's finest luxury wedding venues and vendors.",
      canonicalPath: "/france",
    },
    ai: {
      summary:
        "Premier European destination. Provence, Côte d'Azur, and Châteaux Loire Valley lead market.",
      focusKeywords: [
        "france wedding venues",
        "luxury wedding france",
        "french chateau wedding",
      ],
    },
    regions: [],
  },
  {
    slug: "hungary",
    name: "Hungary",
    iso2: "HU",
    listingCount: 2,
    seo: {
      title: "Luxury Wedding Venues in Hungary | Luxury Wedding Directory",
      metaDescription:
        "Discover curated luxury wedding venues in Hungary. Budapest thermal palaces and Hungarian wine country estates, editorially selected.",
      canonicalPath: "/hungary",
    },
    ai: {
      summary:
        "Budapest's thermal bath palaces, Danube-spanning bridges and Art Nouveau grandeur make Hungary an increasingly sought-after European wedding destination with exceptional value.",
      focusKeywords: [
        "luxury wedding hungary",
        "budapest wedding venue",
        "hungarian castle wedding",
      ],
    },
    regions: [],
  },
  {
    slug: "ireland",
    name: "Ireland",
    iso2: "IE",
    listingCount: 0,
    britishIslesSiblings: ["england", "scotland", "wales", "northern-ireland"],
    seo: {
      title: "Luxury Wedding Venues in Ireland | Luxury Wedding Directory",
      metaDescription:
        "Discover Ireland's finest luxury wedding venues and vendors.",
      canonicalPath: "/ireland",
    },
    ai: {
      summary:
        "Growing luxury destination market. Castle and country estate weddings dominate. Dublin urban market emerging.",
      focusKeywords: [
        "ireland wedding venues",
        "luxury wedding ireland",
      ],
    },
    regions: ["dublin"],
  },
  {
    slug: "spain",
    name: "Spain",
    iso2: "ES",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Spain | Luxury Wedding Directory",
      metaDescription: "Discover Spain's finest luxury wedding venues and vendors.",
      canonicalPath: "/spain",
    },
    ai: {
      summary: "Sun-drenched fincas, Andalusian palaces, and Balearic clifftops. Barcelona and Marbella lead the luxury market.",
      focusKeywords: ["spain wedding venues", "luxury wedding spain", "ibiza wedding venue"],
    },
    regions: [],
  },
  {
    slug: "greece",
    name: "Greece",
    iso2: "GR",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Greece | Luxury Wedding Directory",
      metaDescription: "Discover Greece's finest luxury wedding venues and vendors.",
      canonicalPath: "/greece",
    },
    ai: {
      summary: "Santorini and Mykonos dominate the aspirational market. Whitewashed clifftop ceremonies and Aegean light define the aesthetic.",
      focusKeywords: ["greece wedding venues", "santorini wedding", "luxury wedding greece"],
    },
    regions: [],
  },
  {
    slug: "usa",
    name: "USA",
    iso2: "US",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in the USA | Luxury Wedding Directory",
      metaDescription: "Discover the USA's finest luxury wedding venues and vendors.",
      canonicalPath: "/usa",
    },
    ai: {
      summary: "Vast and diverse market. Napa Valley, The Hamptons, and New York City lead luxury intent. Ranch weddings and coastal estates strong.",
      focusKeywords: ["usa wedding venues", "luxury wedding usa", "napa valley wedding"],
    },
    regions: ["new-york", "california"],
  },
  {
    slug: "uae",
    name: "UAE",
    iso2: UAE_COUNTRY.iso2,
    listingCount: UAE_COUNTRY.listingCount,
    seo: {
      title: UAE_COUNTRY.seoTitleTemplate,
      metaDescription: UAE_COUNTRY.metaDescriptionTemplate,
      canonicalPath: "/uae",
    },
    ai: {
      summary: UAE_COUNTRY.aiSummary,
      focusKeywords: UAE_COUNTRY.focusKeywords,
    },
    regions: UAE_REGIONS_RAW.map(r => r.slug),
  },
  {
    slug: "morocco",
    name: "Morocco",
    iso2: "MA",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Morocco | Luxury Wedding Directory",
      metaDescription: "Discover Morocco's finest luxury wedding venues and vendors. Marrakech riads, Atlas Mountain retreats, and palatial celebrations.",
      canonicalPath: "/morocco",
    },
    ai: {
      summary: "Exotic luxury destination. Marrakech dominates with riad courtyards, palace takeovers, and desert camp celebrations. Strong cultural fusion market.",
      focusKeywords: ["morocco wedding venues", "marrakech wedding", "luxury wedding morocco"],
    },
    regions: ["marrakech"],
  },
  {
    slug: "south-africa",
    name: "South Africa",
    iso2: "ZA",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in South Africa | Luxury Wedding Directory",
      metaDescription: "Discover South Africa's finest luxury wedding venues and vendors. Cape Town winelands, safari lodges, and coastal estates.",
      canonicalPath: "/south-africa",
    },
    ai: {
      summary: "World-class destination market. Cape Town and Winelands lead with vineyard estates. Safari weddings in Greater Kruger a unique draw. Exceptional value for luxury.",
      focusKeywords: ["south africa wedding venues", "cape town wedding", "winelands wedding"],
    },
    regions: ["cape-town", "winelands", "kruger"],
  },
  {
    slug: "sweden",
    name: "Sweden",
    iso2: "SE",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Sweden | Luxury Wedding Directory",
      metaDescription: "Discover Sweden's finest luxury wedding venues and vendors. Scandinavian elegance, archipelago islands, and Nordic design.",
      canonicalPath: "/sweden",
    },
    ai: {
      summary: "Scandinavian minimalist luxury. Stockholm archipelago and castle weddings. Midnight sun summer celebrations and moody winter aesthetics.",
      focusKeywords: ["sweden wedding venues", "stockholm wedding", "scandinavian wedding"],
    },
    regions: ["stockholm"],
  },
  {
    slug: "canada",
    name: "Canada",
    iso2: "CA",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Canada | Luxury Wedding Directory",
      metaDescription: "Discover Canada's finest luxury wedding venues and vendors. Pacific Northwest mountains, Niagara wine country, and urban elegance.",
      canonicalPath: "/canada",
    },
    ai: {
      summary: "Diverse luxury market. Vancouver mountains and Pacific coastline, Toronto urban elegance, Niagara wine country. Whistler and Muskoka premium destinations.",
      focusKeywords: ["canada wedding venues", "vancouver wedding", "luxury wedding canada"],
    },
    regions: ["british-columbia", "ontario"],
  },
  {
    slug: "romania",
    name: "Romania",
    iso2: "RO",
    listingCount: 1,
    seo: {
      title: "Luxury Wedding Venues in Romania | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in Romania, Bucharest palace gardens, Belle Époque orangeries, and Transylvanian estates, editorially selected.",
      canonicalPath: "/romania",
    },
    ai: {
      summary: "Romania offers an emerging luxury wedding market centred on Bucharest's grand Belle Époque architecture, intimate orangeries, and Transylvanian castle estates.",
      focusKeywords: ["luxury wedding romania", "bucharest wedding venue", "romania wedding estate"],
    },
    regions: ["bucharest-and-ilfov"],
  },
  {
    slug: "portugal",
    name: "Portugal",
    iso2: "PT",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Portugal | Luxury Wedding Directory",
      metaDescription: "Discover Portugal's finest luxury wedding venues and vendors. Atlantic light, azulejo tiles, and understated elegance.",
      canonicalPath: "/portugal",
    },
    ai: {
      summary: "Portugal offers understated European luxury with Atlantic coastlines, traditional azulejo tile work, and a growing market for intimate estate celebrations.",
      focusKeywords: ["portugal wedding venues", "luxury wedding portugal", "lisbon wedding"],
    },
    regions: [],
  },
  {
    slug: "indonesia",
    name: "Indonesia",
    iso2: "ID",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Indonesia | Luxury Wedding Directory",
      metaDescription: "Discover Indonesia's finest luxury wedding venues and vendors. Bali temple ceremonies and tropical destination celebrations.",
      canonicalPath: "/indonesia",
    },
    ai: {
      summary: "Indonesia, particularly Bali, offers spiritual temple ceremonies, lush tropical settings, and unique cultural wedding experiences.",
      focusKeywords: ["bali wedding venues", "indonesia wedding", "tropical wedding bali"],
    },
    regions: [],
  },
  {
    slug: "slovenia",
    name: "Slovenia",
    iso2: "SI",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Slovenia | Luxury Wedding Directory",
      metaDescription: "Discover Slovenia's finest luxury wedding venues and vendors. Lake Bled castle ceremonies and Alpine estate celebrations.",
      canonicalPath: "/slovenia",
    },
    ai: {
      summary: "Slovenia offers dramatic Alpine scenery, Lake Bled's iconic island church, and intimate castle and estate venues for exclusive celebrations.",
      focusKeywords: ["slovenia wedding venues", "lake bled wedding", "luxury wedding slovenia"],
    },
    regions: [],
  },
  {
    slug: "turkey",
    name: "Turkey",
    iso2: "TR",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Turkey | Luxury Wedding Directory",
      metaDescription: "Discover Turkey's finest luxury wedding venues and vendors. Cappadocia cave ceremonies and Aegean coastal celebrations.",
      canonicalPath: "/turkey",
    },
    ai: {
      summary: "Turkey combines Byzantine grandeur with Aegean coastal luxury. Cappadocia, Istanbul, and the Bodrum peninsula lead the destination wedding market.",
      focusKeywords: ["turkey wedding venues", "cappadocia wedding", "luxury wedding turkey"],
    },
    regions: [],
  },
  {
    slug: "germany",
    name: "Germany",
    iso2: "DE",
    listingCount: 0,
    seo: {
      title: "Luxury Wedding Venues in Germany | Luxury Wedding Directory",
      metaDescription: "Discover Germany's finest luxury wedding venues and vendors. Bavarian castle ceremonies and vineyard estate celebrations.",
      canonicalPath: "/germany",
    },
    ai: {
      summary: "Germany offers Bavarian castle grandeur, Rhine Valley vineyard estates, and refined city venues for couples seeking European heritage settings.",
      focusKeywords: ["germany wedding venues", "bavarian castle wedding", "luxury wedding germany"],
    },
    regions: [],
  },
];

// ---------------------------------------------------------------------------
// REGIONS
// ---------------------------------------------------------------------------

const ALL_REGIONS_SENTINEL = {
  slug: "all",
  name: "All Regions",
  countrySlug: null,
  group: null,
  priorityLevel: null,
  listingCount: 0,
  heroTitle: null,
  heroSubtitle: null,
  heroImg: null,
  introEditorial: null,
  categoryShortcuts: [],
  cities: [],
  seo: { title: "", metaDescription: "", canonicalPath: "" },
  ai: {
    summary: "",
    focusKeywords: [],
    intentSignals: { high: [], mid: [], low: [] },
    schemaType: null,
  },
  relatedRegionSlugs: [],
};

// --- Generic country region mapper -----------------------------------------
// Add a new country: import its regions + cities, call mapCountryRegions().

function mapCountryRegions(regions, cities, { countrySlug, countryName }) {
  return regions.filter(r => !r.isLegacy).map(r => ({
    slug: r.slug,
    name: r.name,
    countrySlug,
    group: countryName,
    priorityLevel: r.priorityLevel,
    listingCount: r.listingCount,
    heroTitle: r.heroSubtitle ? null : `Weddings in ${r.name}`,
    heroSubtitle: r.heroSubtitle || null,
    heroImg: null,
    introEditorial: r.description,
    seoText: r.seoText || null,
    lat: r.lat ?? null,
    lng: r.lng ?? null,
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: cities.filter(c => c.regionSlug === r.slug).map(c => c.slug),
    seo: {
      title: `Luxury Wedding Venues in ${r.name}, ${countryName} | Luxury Wedding Directory`,
      metaDescription: r.description,
      canonicalPath: r.canonicalRoute,
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary: r.aiSummary,
      focusKeywords: r.focusKeywords,
      intentSignals: r.intentSignals,
      schemaType: "Place",
      ...(r.ai ?? {}),
    },
    relatedRegionSlugs: [],
  }));
}

// --- Italy regions ---------------------------------------------------------

const ITALY_REGIONS_MAPPED = mapCountryRegions(ITALY_REGIONS, ITALY_CITIES, {
  countrySlug: "italy",
  countryName: "Italy",
});

// --- Full-content UK / Ireland regions ------------------------------------

const UK_IRELAND_FULL_REGIONS = [
  // London
  {
    slug: "london",
    name: "London",
    countrySlug: "england",
    group: "England",
    priorityLevel: "primary",
    listingCount: 12,
    lat: 51.507, lng: -0.127,
    heroTitle: "Weddings in London",
    heroSubtitle: "From Mayfair townhouses to skyline terraces, the United Kingdom's most distinguished collection of luxury wedding venues.",
    heroImg:
      "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=1600&q=80",
    introEditorial:
      "London commands a position no other British city can claim. Twelve distinct districts, each with its own character, deliver over two hundred licensed wedding venues ranging from Grade I–listed Georgian townhouses and private members' clubs to contemporary riverside terraces and converted power stations. The capital concentrates the United Kingdom's finest caterers, florists, photographers and planners within a single metropolitan area, precision, prestige and an unmatched density of excellence.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    localTerm: "Districts",
    trustSignals: [
      "Central London",
      "West London",
      "City of London",
      "Civil & Religious Licences",
      "Historic Royal Borough Venues",
      "Private Members' Clubs",
    ],
    cities: ["mayfair", "chelsea", "kensington", "the-city", "hampstead", "shoreditch", "richmond", "notting-hill", "south-bank", "belgravia"],
    seo: {
      title: "Luxury Wedding Venues in London | Luxury Wedding Directory",
      metaDescription:
        "Discover London's finest curated luxury wedding venues, planners, photographers and vendors. From Mayfair townhouses to Thames-side terraces.",
      canonicalPath: "/england/london",
      ogTitle: "London Wedding Venues & Vendors | Luxury Wedding Directory",
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary:
        "Highest search volume UK county for wedding venues. Year-round demand with spring/autumn peaks. Mayfair, Chelsea and South Bank dominate luxury tier.",
      focusKeywords: [
        "london wedding venues",
        "luxury wedding london",
        "wedding planner london",
        "mayfair wedding venue",
      ],
      intentSignals: {
        high: [
          "book wedding venue london",
          "london wedding planner hire",
        ],
        mid: [
          "best wedding venues london",
          "luxury wedding london ideas",
        ],
        low: [
          "london wedding inspiration",
          "getting married in london",
        ],
      },
      positioning: "The world's most complete wedding city, every style, every scale, every supplier within a single metropolitan area.",
      opening_line: "London has no single character, and that's the point. A Mayfair townhouse is not a Shoreditch warehouse. Both are correct answers.",
      differentiation: "London versus Paris: London has more venue diversity and better supply chains. Paris has more romance. The couple who knows what they want chooses London; the couple who wants to be moved chooses Paris.",
      register: "urban-chic",
      style_affinity: ["urban-prestige", "historic-ceremony", "understated-elegant", "intimate-garden"],
      season_peak: ["april", "may", "june", "september", "october"],
      season_avoid: [],
      scale_fit: "all",
      not_for: ["vineyard", "rustic-farmhouse", "coastal-dramatic", "wild-remote"],
      price_index: "premium",
      cross_sell: ["surrey", "cotswolds", "paris"],
      schemaType: "Place",
    },
    relatedRegionSlugs: ["surrey", "kent", "essex", "hertfordshire", "berkshire", "hampshire", "buckinghamshire", "oxfordshire"],

    // ── E-E-A-T Editorial Content (AI-generated on backend) ──────────────
    editorial: {
      headline: "The Capital of British Celebration",
      standfirst: "Three centuries of hosting the grandest celebrations in Europe. Eighteen months of editorial verification. Every venue on this page earned its place.",
      sections: [
        {
          heading: "A City of Unmatched Venue Diversity",
          body: "No other city on earth offers the breadth of wedding settings that London delivers. Within a single square mile you can choose between a Grade I–listed Georgian townhouse, a converted Victorian power station, a rooftop terrace overlooking the Thames, or a members-only garden square accessible to fewer than two hundred families. This density of choice is London's defining advantage, and it's why couples travel from over forty countries each year to marry here. From the gilded state rooms of Spencer House in Mayfair to the wisteria-draped pergola hidden above Hampstead Heath, every neighbourhood has its own character, its own light, and its own story to tell.",
        },
        {
          heading: "The LWD Verification Standard",
          body: "Every venue listed on this page has been personally visited, photographed and assessed by our editorial team. We evaluate over thirty criteria including natural light quality, acoustics, catering flexibility, accessibility, overnight accommodation, and, critically, how the venue performs on the day itself, not just in marketing materials. We reject approximately sixty percent of the venues we visit. Those that make our directory represent the genuine best of London.",
        },
        {
          heading: "Neighbourhood Guide for Couples",
          body: "Mayfair and Belgravia command the ultra-luxury tier, expect five-star hotels, private members' clubs, and starting prices above £25,000. Chelsea and South Bank deliver a blend of heritage and contemporary cool, ideal for couples who want tradition without formality. Hampstead and Richmond offer rare green space for garden ceremonies within zone two, while Shoreditch and King's Cross cater to the creative couple seeking industrial-chic warehouse settings with bespoke catering. Each neighbourhood shapes the tone of your celebration before a single flower is arranged.",
        },
        {
          heading: "Seasonal Considerations & Insider Advice",
          body: "London's peak wedding season runs April through September, with Saturday dates at premium venues booking twelve to eighteen months ahead. Autumn weddings, particularly October, offer a sweet spot: warm golden light, lower venue rates, and the capital's parks ablaze with colour. Winter weddings in December and January unlock dramatic settings at significantly reduced cost, and several venues on our list offer exclusive Christmas and New Year packages. Our editorial recommendation: consider a Thursday or Friday celebration for thirty to forty percent savings at identical venues.",
        },
      ],
      expertNote: {
        author: "LWD Editorial Team",
        credential: "18 months · 200+ venue visits · London",
        quote: "London is the only city where we've found venues that genuinely surprise us on every visit. The depth of quality here, from a hidden Jacobean hall in the City to a modernist gallery in Mayfair, is without equal.",
      },
      lastVerified: "February 2026",
    },
  },

  // Surrey
  {
    slug: "surrey",
    name: "Surrey",
    countrySlug: "england",
    group: "England",
    priorityLevel: "primary",
    listingCount: 0,
    lat: 51.232, lng: -0.585,
    heroTitle: "Weddings in Surrey",
    heroSubtitle: "Country estates and rolling hills on London's doorstep",
    heroImg:
      "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1600&q=80",
    introEditorial:
      "Surrey offers the perfect blend of countryside beauty and metropolitan convenience, making it one of England's most sought-after wedding destinations. Just thirty minutes from central London, the county unfolds into a landscape of ancient woodlands, manicured estates and quintessentially English villages. From the grandeur of Clandon Park and the romantic gardens of Loseley Park to boutique barn conversions in the Surrey Hills and elegant golf club settings overlooking the North Downs, the county caters to every vision. Surrey's proximity to London means access to the capital's finest vendors while celebrating in genuinely rural surroundings \u2014 a combination that makes it irresistible for discerning couples.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["guildford", "farnham", "dorking"],
    seo: {
      title: "Luxury Wedding Venues in Surrey | Luxury Wedding Directory",
      metaDescription:
        "Discover Surrey's finest luxury wedding venues and vendors. Country estates, boutique barns and elegant settings on London's doorstep.",
      canonicalPath: "/england/surrey",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary:
        "Premium commuter-belt county. Strong country estate and barn conversion market. London overflow demand significant.",
      focusKeywords: [
        "surrey wedding venues",
        "luxury wedding surrey",
        "country house wedding surrey",
      ],
      intentSignals: {
        high: ["surrey wedding venue hire", "surrey wedding planner"],
        mid: ["best wedding venues surrey"],
        low: ["surrey wedding ideas"],
      },
      schemaType: "Place",
    },
    relatedRegionSlugs: ["london", "kent", "hampshire", "berkshire", "sussex"],
  },

  // Cotswolds
  {
    slug: "cotswolds",
    name: "Cotswolds",
    countrySlug: "england",
    group: "England",
    lat: 51.800, lng: -1.700,
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in the Cotswolds",
    heroSubtitle: "Honey-stone manor houses, walled gardens and England's softest countryside.",
    heroImg: "https://images.unsplash.com/photo-1560969184-10fe8719e047?auto=format&fit=crop&w=1600&q=80",
    introEditorial: "The Cotswolds is England at its most quietly beautiful. Villages of honey-coloured limestone, walled kitchen gardens, meadows that turn gold in early summer, and manor houses that have been hosting celebrations for centuries. This is a landscape that doesn't need to announce itself. The light does it instead, soft, warm, and entirely unlike anywhere else in England. Weddings here tend toward the intimate and the considered: long tables in converted barns, ceremonies in private chapel grounds, receptions that spill out onto terrace lawns at dusk. Couples who choose the Cotswolds rarely regret it. Those who want somewhere easier usually choose somewhere else first.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["chipping-campden", "bourton-on-the-water", "burford", "cirencester", "tetbury"],
    localTerm: "Villages",
    seo: {
      title: "Luxury Wedding Venues in the Cotswolds | Luxury Wedding Directory",
      metaDescription: "Discover the Cotswolds' finest luxury wedding venues, honey-stone manor houses, walled gardens and intimate countryside estates personally verified by the LWD editorial team.",
      canonicalPath: "/england/cotswolds",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary: "England's most sought-after rural wedding county. Manor houses and barn conversions dominate. Strong demand from London couples seeking countryside without long travel.",
      focusKeywords: [
        "cotswolds wedding venues",
        "luxury wedding cotswolds",
        "manor house wedding cotswolds",
        "barn wedding cotswolds",
      ],
      intentSignals: {
        high: ["cotswolds wedding venue hire", "cotswolds wedding planner"],
        mid: ["best wedding venues cotswolds", "country house wedding cotswolds"],
        low: ["cotswolds wedding ideas", "getting married in the cotswolds"],
      },
      positioning: "England's softest landscape, honey-stone manors, walled gardens, and a warmth that feels entirely private.",
      register: "romantic-intimate",
      style_affinity: ["rustic-luxe", "intimate-garden", "romantic", "historic-ceremony"],
      season_peak: ["may", "june", "july", "august", "september"],
      season_avoid: ["january", "february"],
      scale_fit: "intimate",
      not_for: ["ultra-modern", "coastal-dramatic", "urban-industrial", "vineyard"],
      price_index: "premium",
      cross_sell: ["surrey", "oxfordshire", "london"],
      schemaType: "Place",
    },
    relatedRegionSlugs: ["surrey", "oxfordshire", "berkshire", "warwickshire"],
  },

  // Edinburgh
  {
    slug: "edinburgh",
    name: "Edinburgh",
    countrySlug: "scotland",
    group: "Scotland",
    lat: 55.953, lng: -3.188,
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in Edinburgh",
    heroSubtitle: "Castle spires, Georgian grandeur and Scottish romance",
    heroImg:
      "https://images.unsplash.com/photo-1506377585622-bedcbb027afc?auto=format&fit=crop&w=1600&q=80",
    introEditorial:
      "Edinburgh is one of the most romantic cities in the world for a wedding. The dramatic silhouette of Edinburgh Castle presiding over the Old Town, the sweeping Georgian crescents of the New Town, and the wild beauty of Arthur's Seat create a backdrop that no other British city can rival. Celebrate in a medieval castle, a Georgian assembly room, a Michelin-starred restaurant or a contemporary gallery space \u2014 Edinburgh offers extraordinary range within a compact, walkable city. Scottish wedding traditions add a layer of ceremony and emotion that guests never forget, from pipers greeting arrivals to ceilidh dancing that brings everyone together. The city's world-class hotels, renowned chefs and thriving creative scene ensure every detail meets the highest standard.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["old-town", "new-town", "leith"],
    seo: {
      title: "Luxury Wedding Venues in Edinburgh | Luxury Wedding Directory",
      metaDescription:
        "Discover Edinburgh's finest luxury wedding venues and vendors. From castle celebrations to Georgian grandeur in Scotland's capital.",
      canonicalPath: "/scotland/edinburgh",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary:
        "Scotland's premier wedding city. Castle and Georgian venue market. Strong international demand from US and Europe. Hogmanay and Festival seasons create unique opportunities.",
      focusKeywords: [
        "edinburgh wedding venues",
        "luxury wedding edinburgh",
        "castle wedding edinburgh",
        "scottish wedding",
      ],
      intentSignals: {
        high: [
          "edinburgh castle wedding venue",
          "edinburgh wedding planner hire",
        ],
        mid: ["best edinburgh wedding venues"],
        low: [
          "edinburgh wedding ideas",
          "scottish wedding traditions",
        ],
      },
      schemaType: "Place",
    },
    relatedRegionSlugs: ["highlands"],
  },

  // Cardiff
  {
    slug: "cardiff",
    name: "Cardiff",
    countrySlug: "wales",
    group: "Wales",
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in Cardiff",
    heroSubtitle: "Welsh heritage and cosmopolitan elegance",
    heroImg:
      "https://images.unsplash.com/photo-1558981806-ec527fa84c39?auto=format&fit=crop&w=1600&q=80",
    introEditorial:
      "Cardiff combines the warmth of Welsh hospitality with a cosmopolitan energy that makes it a compelling wedding destination. The Welsh capital offers everything from fairy-tale castle celebrations at Cardiff Castle and Castell Coch to sleek waterfront venues in Cardiff Bay and intimate country house settings in the surrounding Vale of Glamorgan. The city's renowned food scene, vibrant arts culture and passionate musical heritage create a celebration atmosphere that is uniquely Welsh. With excellent transport links and a growing reputation for luxury hospitality, Cardiff is emerging as one of the UK's most exciting wedding destinations.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["cardiff-bay", "vale-of-glamorgan"],
    seo: {
      title: "Luxury Wedding Venues in Cardiff | Luxury Wedding Directory",
      metaDescription:
        "Discover Cardiff's finest luxury wedding venues and vendors. Castle celebrations, waterfront venues and Welsh hospitality.",
      canonicalPath: "/wales/cardiff",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary:
        "Wales premier wedding city. Castle and waterfront venues lead. Growing luxury market. Strong local and English border demand.",
      focusKeywords: [
        "cardiff wedding venues",
        "luxury wedding cardiff",
        "welsh wedding",
      ],
      intentSignals: {
        high: ["cardiff castle wedding venue"],
        mid: ["best cardiff wedding venues"],
        low: ["cardiff wedding ideas"],
      },
      schemaType: "Place",
    },
    relatedRegionSlugs: ["pembrokeshire"],
  },

  // Dublin
  {
    slug: "dublin",
    name: "Dublin",
    countrySlug: "ireland",
    group: "Ireland",
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in Dublin",
    heroSubtitle: "Georgian elegance and Irish warmth",
    heroImg:
      "https://images.unsplash.com/photo-1549918864-48ac978761a4?auto=format&fit=crop&w=1600&q=80",
    introEditorial:
      "Dublin offers a wedding experience steeped in history, warmth and genuine Irish hospitality. From Georgian townhouse celebrations on Merrion Square to grand hotel ballrooms overlooking St Stephen's Green and converted industrial spaces in the Docklands, the Irish capital delivers sophistication with soul. Dublin's legendary food scene \u2014 from Michelin-starred restaurants to artisan producers \u2014 ensures every wedding feast is memorable. The city's natural gift for celebration, its world-famous music tradition and the unmatched warmth of Irish welcome create an atmosphere that international guests consistently describe as the best wedding they've ever attended.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["city-centre", "dalkey", "howth"],
    seo: {
      title: "Luxury Wedding Venues in Dublin | Luxury Wedding Directory",
      metaDescription:
        "Discover Dublin's finest luxury wedding venues and vendors. Georgian elegance, world-class dining and legendary Irish hospitality.",
      canonicalPath: "/ireland/dublin",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary:
        "Ireland's premier wedding city. Georgian and grand hotel venue market. Strong transatlantic demand from US-Irish diaspora.",
      focusKeywords: [
        "dublin wedding venues",
        "luxury wedding dublin",
        "irish wedding",
      ],
      intentSignals: {
        high: ["dublin wedding venue hire", "dublin wedding planner"],
        mid: ["best dublin wedding venues"],
        low: ["dublin wedding ideas"],
      },
      schemaType: "Place",
    },
    relatedRegionSlugs: [],
  },

  // Scottish Highlands
  {
    slug: "highlands",
    name: "Scottish Highlands",
    countrySlug: "scotland",
    group: "Scotland",
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in the Scottish Highlands",
    heroSubtitle: "Wild beauty, ancient estates and a solitude that makes a wedding feel genuinely momentous.",
    heroImg: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=80",
    introEditorial: "The Scottish Highlands deliver something no landscaped estate can manufacture: a landscape so elemental it changes the emotional register of everything that happens within it. Ancient castles sit above mirror lochs. Deer move across moorland at dusk. The light in June lasts until eleven at night and turns the hills purple and gold. Weddings here tend toward the intimate and the considered, castle banquets, loch-side ceremonies, long evenings beside stone fireplaces. Couples who choose the Highlands rarely want for scale. They want for meaning.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["inverness", "fort-william", "aviemore"],
    localTerm: "Areas",
    seo: {
      title: "Luxury Wedding Venues in the Scottish Highlands | Luxury Wedding Directory",
      metaDescription: "Discover the Scottish Highlands' finest luxury wedding venues, ancient castles, lochside estates and wild landscapes for an unforgettable celebration.",
      canonicalPath: "/scotland/highlands",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary: "Wild luxury destination. Castle and lochside estate market. Strong international demand from US couples seeking authentic Scottish experience.",
      focusKeywords: ["scottish highlands wedding", "highland wedding venue", "castle wedding scotland", "lochside wedding"],
      intentSignals: {
        high: ["highland castle wedding venue", "scottish highlands wedding planner"],
        mid: ["best scottish highlands wedding venues"],
        low: ["highland wedding ideas", "getting married in scotland"],
      },
      positioning: "Wild beauty, ancient estates and a solitude that makes a wedding feel genuinely momentous.",
      opening_line: "The Highlands don't ask couples to be moved by them, they simply deliver the conditions in which being moved is unavoidable.",
      differentiation: "Highlands versus Lake District: The Lake District is more accessible, more cultivated, and more constrained. The Highlands are wilder, more remote, and the setting is more extreme. Couples who want true isolation choose the Highlands.",
      register: "wild-remote",
      style_affinity: ["wild-remote", "historic-ceremony", "rustic-luxe", "intimate"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["urban-prestige", "coastal-dramatic", "vineyard", "ultra-modern"],
      price_index: "mid",
      cross_sell: ["lake-district", "edinburgh", "cotswolds"],
      schemaType: "Place",
    },
    relatedRegionSlugs: ["edinburgh"],
  },

  // Lake District
  {
    slug: "lake-district",
    name: "Lake District",
    countrySlug: "england",
    group: "England",
    priorityLevel: "primary",
    listingCount: 0,
    heroTitle: "Weddings in the Lake District",
    heroSubtitle: "England's most dramatic national park, fells, mirror lakes and stone-built estates with 300 years of literary history.",
    heroImg: "https://images.unsplash.com/photo-1571983822636-97a6a23e1f5b?auto=format&fit=crop&w=1600&q=80",
    introEditorial: "The Lake District is England at its most atmospheric. Windermere and Ullswater reflect the fells like mirrors. Stone country houses sit above the water. The light in late summer, angled, golden, heavy, has been inspiring painters and poets since Wordsworth settled here in 1799. Weddings in the Lake District tend to be intimate and unhurried: lakeside ceremonies, long tables in converted barns, evenings that stretch with the northern light. The landscape does the heavy lifting. The venues understand this and don't compete with it.",
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: ["windermere", "ambleside", "keswick", "ullswater"],
    localTerm: "Villages",
    seo: {
      title: "Luxury Wedding Venues in the Lake District | Luxury Wedding Directory",
      metaDescription: "Discover the Lake District's finest luxury wedding venues, lakeside estates, stone country houses and dramatic fell landscapes for an intimate celebration.",
      canonicalPath: "/england/lake-district",
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary: "England's premier wild landscape wedding destination. Lakeside and country house market. Strong demand for exclusive-use properties.",
      focusKeywords: ["lake district wedding venues", "windermere wedding venue", "lake district wedding", "cumbria wedding"],
      intentSignals: {
        high: ["lake district wedding venue hire", "windermere wedding venue book"],
        mid: ["best lake district wedding venues", "lakeside wedding england"],
        low: ["lake district wedding ideas", "getting married in the lake district"],
      },
      positioning: "England's wildest national park, fells, mirror lakes and a landscape that shaped Wordsworth's imagination.",
      opening_line: "The Lake District does something no manicured estate can replicate: it makes the landscape the ceremony. The mountains are always in the background, always present.",
      differentiation: "Lake District versus Scottish Highlands: The Highlands are wilder and more remote. The Lake District is more accessible, better connected, and has a stronger literary heritage. Couples who want wildness but need guests to actually get there choose the Lake District.",
      register: "wild-remote",
      style_affinity: ["wild-remote", "rustic-luxe", "intimate", "romantic"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["urban-prestige", "coastal-dramatic", "vineyard", "ultra-modern"],
      price_index: "mid",
      cross_sell: ["highlands", "yorkshire", "cotswolds"],
      schemaType: "Place",
    },
    relatedRegionSlugs: ["yorkshire", "cotswolds"],
  },
];

// --- Skeleton UK / Ireland regions ----------------------------------------

function skeletonRegion(slug, name, countrySlug, group, relatedRegionSlugs = []) {
  const canonicalPrefix = `/${countrySlug}`;
  return {
    slug,
    name,
    countrySlug,
    group,
    priorityLevel: "secondary",
    listingCount: 0,
    heroTitle: `Weddings in ${name}`,
    heroSubtitle: null,
    heroImg: null,
    introEditorial: `Discover luxury wedding venues and vendors in ${name}.`,
    categoryShortcuts: VENDOR_CATEGORIES.map(vc => ({
      categorySlug: vc.slug,
      label: vc.label,
      count: 0,
    })),
    cities: [],
    seo: {
      title: `Luxury Wedding Venues in ${name} | Luxury Wedding Directory`,
      metaDescription: `Discover ${name}'s finest luxury wedding venues and vendors.`,
      canonicalPath: `${canonicalPrefix}/${slug}`,
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
    },
    ai: {
      summary: "",
      focusKeywords: [`${slug} wedding venues`],
      intentSignals: { high: [], mid: [], low: [] },
      schemaType: "Place",
    },
    relatedRegionSlugs,
  };
}

const UK_IRELAND_SKELETON_REGIONS = [
  // England
  skeletonRegion("kent", "Kent", "england", "England", ["london", "surrey", "sussex"]),
  skeletonRegion("sussex", "Sussex", "england", "England", ["surrey", "kent", "hampshire"]),
  skeletonRegion("hampshire", "Hampshire", "england", "England", ["surrey", "dorset", "wiltshire"]),
  skeletonRegion("berkshire", "Berkshire", "england", "England", ["surrey", "oxfordshire", "buckinghamshire"]),
  skeletonRegion("buckinghamshire", "Buckinghamshire", "england", "England", ["berkshire", "oxfordshire", "hertfordshire"]),
  skeletonRegion("oxfordshire", "Oxfordshire", "england", "England", ["berkshire", "buckinghamshire", "gloucestershire"]),
  skeletonRegion("essex", "Essex", "england", "England", ["london", "hertfordshire", "suffolk"]),
  skeletonRegion("hertfordshire", "Hertfordshire", "england", "England", ["london", "essex", "buckinghamshire"]),
  skeletonRegion("devon", "Devon", "england", "England", ["cornwall", "somerset", "dorset"]),
  skeletonRegion("cornwall", "Cornwall", "england", "England", ["devon"]),
  skeletonRegion("somerset", "Somerset", "england", "England", ["devon", "dorset", "wiltshire"]),
  skeletonRegion("dorset", "Dorset", "england", "England", ["hampshire", "somerset", "wiltshire"]),
  skeletonRegion("wiltshire", "Wiltshire", "england", "England", ["hampshire", "dorset", "gloucestershire"]),
  skeletonRegion("gloucestershire", "Gloucestershire", "england", "England", ["oxfordshire", "wiltshire", "bristol"]),
  skeletonRegion("suffolk", "Suffolk", "england", "England", ["essex", "norfolk", "cambridgeshire"]),
  skeletonRegion("norfolk", "Norfolk", "england", "England", ["suffolk", "cambridgeshire"]),
  skeletonRegion("cambridgeshire", "Cambridgeshire", "england", "England", ["suffolk", "norfolk", "hertfordshire"]),
  skeletonRegion("bristol", "Bristol", "england", "England", ["gloucestershire", "somerset"]),
  skeletonRegion("warwickshire", "Warwickshire", "england", "England", ["staffordshire", "leicestershire", "oxfordshire"]),
  skeletonRegion("staffordshire", "Staffordshire", "england", "England", ["cheshire", "derbyshire", "warwickshire"]),
  skeletonRegion("cheshire", "Cheshire", "england", "England", ["manchester", "staffordshire", "shropshire"]),
  skeletonRegion("manchester", "Manchester", "england", "England", ["cheshire", "lancashire", "derbyshire"]),
  skeletonRegion("lancashire", "Lancashire", "england", "England", ["manchester", "cumbria", "yorkshire"]),
  skeletonRegion("derbyshire", "Derbyshire", "england", "England", ["staffordshire", "nottinghamshire", "leicestershire"]),
  skeletonRegion("leicestershire", "Leicestershire", "england", "England", ["warwickshire", "derbyshire", "nottinghamshire"]),
  skeletonRegion("nottinghamshire", "Nottinghamshire", "england", "England", ["derbyshire", "leicestershire", "yorkshire"]),
  skeletonRegion("shropshire", "Shropshire", "england", "England", ["cheshire", "staffordshire"]),
  skeletonRegion("liverpool", "Liverpool", "england", "England", ["cheshire", "lancashire", "manchester"]),
  skeletonRegion("yorkshire", "Yorkshire", "england", "England", ["lancashire", "derbyshire", "durham"]),
  skeletonRegion("northumberland", "Northumberland", "england", "England", ["durham", "cumbria"]),
  skeletonRegion("durham", "Durham", "england", "England", ["northumberland", "yorkshire", "cumbria"]),
  skeletonRegion("cumbria", "Cumbria", "england", "England", ["lancashire", "northumberland", "durham"]),

  // Scotland, skeleton (Highlands promoted to full entry in UK_IRELAND_FULL_REGIONS)

  // Wales
  skeletonRegion("pembrokeshire", "Pembrokeshire", "wales", "Wales", ["cardiff"]),

  // Northern Ireland
  skeletonRegion("belfast", "Belfast", "northern-ireland", "Northern Ireland", []),
];

// --- France regions --------------------------------------------------------

const FRANCE_REGIONS = [
  {
    slug: "provence",
    name: "Provence",
    countrySlug: "france",
    group: "France",
    priorityLevel: 1,
    listingCount: 0,
    heroTitle: "Weddings in Provence",
    heroSubtitle: "Lavender fields, hilltop villages and sun-drenched châteaux in the south of France.",
    heroImg: null,
    introEditorial: "Provence is one of Europe's most romantic wedding destinations, a landscape of lavender, limestone and ancient light. From the hilltop perch of Gordes to the grand allées of the Luberon, the region offers an unmatched combination of natural beauty and refined venue stock. Weddings here are unhurried, sensory affairs: long tables beneath century-old planes, rosé poured at golden hour, the scent of wild herbs in evening air.",
    categoryShortcuts: [],
    cities: ["gordes", "luberon", "aix-en-provence", "avignon", "les-baux-de-provence", "saint-remy-de-provence"],
    localTerm: "Areas",
    seo: {
      title: "Luxury Wedding Venues in Provence, France | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in Provence, from hilltop châteaux in the Luberon to elegant estates near Aix-en-Provence. Personally verified by the LWD editorial team.",
      canonicalPath: "/france/provence",
    },
    ai: {
      summary: "Provence, lavender fields, hilltop villages and sun-drenched châteaux in southern France.",
      focusKeywords: ["luxury wedding provence", "provence wedding venue", "chateau wedding provence", "luberon wedding", "aix-en-provence wedding"],
      positioning: "Lavender light and stone farmhouses. The most relaxed luxury on the continent.",
      opening_line: "Provence weddings stretch until midnight. The food is serious, the wine is local, and the light in July turns everything the colour of honey.",
      differentiation: "Provence versus Tuscany: Tuscany is more cinematic, more formal, more photographed. Provence is more lived-in, more sun-bleached, more relaxed. Couples who want guests to have a brilliant time choose Provence.",
      register: "sun-social",
      style_affinity: ["vineyard", "rustic-luxe", "sun-social", "romantic", "intimate-garden"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "all",
      not_for: ["urban-prestige", "coastal-dramatic", "historic-ceremony", "ultra-modern"],
      price_index: "premium",
      cross_sell: ["tuscany", "paris", "loire-valley"],
    },
    relatedRegionSlugs: ["paris", "dordogne", "normandy"],
  },
  {
    slug: "paris",
    name: "Paris",
    countrySlug: "france",
    group: "France",
    priorityLevel: 1,
    listingCount: 0,
    heroTitle: "Weddings in Paris",
    heroSubtitle: "Iconic city weddings in the world's most romantic capital.",
    heroImg: null,
    introEditorial: "Paris remains the gold standard for romantic city weddings, from grand Haussmann ballrooms and private hôtels particuliers to intimate Left Bank venues overlooking the Seine.",
    categoryShortcuts: [],
    cities: ["marais", "saint-germain", "montmartre", "champs-elysees"],
    localTerm: "Arrondissements",
    seo: {
      title: "Luxury Wedding Venues in Paris, France | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in Paris, from grand ballrooms and private hôtels particuliers to intimate Left Bank venues.",
      canonicalPath: "/france/paris",
    },
    ai: {
      summary: "Paris, iconic city weddings in grand ballrooms and private hôtels particuliers.",
      focusKeywords: ["luxury wedding paris", "paris wedding venue", "chateau wedding paris", "wedding venue france"],
      positioning: "Culture, grandeur, and the only city in the world where the backdrop outdoes the flowers.",
      opening_line: "Paris concentrates more wedding mythology per square kilometre than anywhere on earth, and the venues know it. The question is whether the couple is ready to hold their own in that setting.",
      differentiation: "Paris versus London: Paris moves you; London equips you. Paris is for the couple who wants to feel something enormous. London is for the couple who wants everything done perfectly.",
      register: "urban-chic",
      style_affinity: ["urban-prestige", "romantic", "historic-ceremony", "understated-elegant"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["july", "august"],
      scale_fit: "grand",
      not_for: ["rustic-farmhouse", "vineyard", "wild-remote", "coastal-dramatic"],
      price_index: "ultra",
      cross_sell: ["provence", "loire-valley", "london"],
    },
    relatedRegionSlugs: ["provence", "normandy", "loire-valley"],
  },
  {
    slug: "loire-valley",
    name: "Loire Valley",
    countrySlug: "france",
    group: "France",
    priorityLevel: 2,
    listingCount: 0,
    heroTitle: "Weddings in the Loire Valley",
    heroSubtitle: "Fairy-tale châteaux along the royal river of France.",
    heroImg: null,
    introEditorial: "The Loire Valley, UNESCO-listed for its concentration of royal châteaux and Renaissance gardens, is France's premier castle-wedding destination.",
    categoryShortcuts: [],
    cities: ["tours", "amboise", "blois", "saumur"],
    localTerm: "Towns",
    seo: {
      title: "Luxury Wedding Venues in Loire Valley, France | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in the Loire Valley, fairy-tale châteaux and royal gardens.",
      canonicalPath: "/france/loire-valley",
    },
    ai: {
      summary: "Loire Valley, fairy-tale châteaux and Renaissance gardens along the royal river.",
      focusKeywords: ["chateau wedding loire valley", "luxury wedding loire", "france chateau wedding"],
      positioning: "Three hundred châteaux along one river. Renaissance France at its most concentrated and most devoted to ceremony.",
      opening_line: "The Loire Valley does not do restraint. Chambord has 440 rooms. Chenonceau spans a river. The region calibrates luxury at a different scale, and the right couple rises to meet it.",
      differentiation: "Loire Valley versus Provence: Provence is more relaxed, more sun-drenched, more about the guests having a brilliant time. The Loire is more formal, more architectural, more about the occasion having weight and history.",
      register: "historic-ceremonial",
      style_affinity: ["historic-ceremony", "romantic-grand", "understated-elegant", "romantic"],
      season_peak: ["may", "june", "september", "october"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "grand",
      not_for: ["rustic-farmhouse", "wild-remote", "coastal-dramatic", "urban-industrial"],
      price_index: "premium",
      cross_sell: ["paris", "provence", "normandy"],
    },
    relatedRegionSlugs: ["paris", "normandy"],
  },
  {
    slug: "normandy",
    name: "Normandy",
    countrySlug: "france",
    group: "France",
    priorityLevel: 3,
    listingCount: 0,
    heroTitle: "Weddings in Normandy",
    heroSubtitle: "Clifftop manors and apple orchards in north-west France.",
    heroImg: null,
    introEditorial: "Normandy's dramatic coastline, half-timbered manor houses and rolling bocage countryside make it an increasingly sought-after wedding destination for couples looking beyond Paris.",
    categoryShortcuts: [],
    cities: ["honfleur", "deauville", "etretat", "bayeux"],
    localTerm: "Towns",
    seo: {
      title: "Luxury Wedding Venues in Normandy, France | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in Normandy, clifftop manors, apple orchards and coastal estates.",
      canonicalPath: "/france/normandy",
    },
    ai: {
      summary: "Normandy, clifftop manors and dramatic coastline in north-west France.",
      focusKeywords: ["wedding normandy france", "chateau wedding normandy", "manor house wedding france"],
      positioning: "Clifftop manor houses, apple orchards and a northern light more painterly than anything south of the Loire.",
      opening_line: "Normandy is for couples who want France without the competition. The châteaux are there, the food is serious, and the cliffs are genuinely dramatic, but nobody is competing for them.",
      differentiation: "Normandy versus Provence: Provence is warmer, more famous, more sun-certain. Normandy is greener, closer to London, and available on dates when Provence is either booked or overheated.",
      register: "romantic-intimate",
      style_affinity: ["rustic-luxe", "romantic", "historic-ceremony", "intimate-garden"],
      season_peak: ["may", "june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "all",
      not_for: ["urban-prestige", "vineyard", "ultra-modern"],
      price_index: "mid",
      cross_sell: ["paris", "loire-valley", "dordogne"],
    },
    relatedRegionSlugs: ["paris", "loire-valley"],
  },
  {
    slug: "dordogne",
    name: "Dordogne",
    countrySlug: "france",
    group: "France",
    priorityLevel: 3,
    listingCount: 0,
    heroTitle: "Weddings in the Dordogne",
    heroSubtitle: "Medieval castles and golden stone villages in south-west France.",
    heroImg: null,
    introEditorial: "The Dordogne, La Périgord, is a pastoral idyll of golden limestone, walnut groves and medieval hilltop bastides. Its concentration of restored châteaux and farmhouse estates makes it a favourite for multi-day destination weddings.",
    categoryShortcuts: [],
    cities: ["sarlat", "bergerac", "perigueux"],
    localTerm: "Towns",
    seo: {
      title: "Luxury Wedding Venues in Dordogne, France | Luxury Wedding Directory",
      metaDescription: "Discover curated luxury wedding venues in the Dordogne, medieval castles and golden stone estates in south-west France.",
      canonicalPath: "/france/dordogne",
    },
    ai: {
      summary: "Dordogne, medieval castles and golden stone villages in south-west France.",
      focusKeywords: ["wedding dordogne france", "chateau wedding dordogne", "perigord wedding"],
      positioning: "Golden limestone, walnut groves and medieval hilltop bastides. The most pastoral luxury in France and among the best value for exclusive-use château hire.",
      opening_line: "The Dordogne rewards couples who are not in a hurry. The landscapes are agricultural, the pace is slow, and the châteaux are genuinely ancient rather than renovated to hotel standard.",
      differentiation: "Dordogne versus Provence: Provence is more Mediterranean, more famous, more visited. The Dordogne is quieter, cooler, and significantly better value for exclusive-use château hire.",
      register: "romantic-intimate",
      style_affinity: ["rustic-luxe", "romantic", "historic-ceremony", "intimate-garden"],
      season_peak: ["june", "july", "august", "september"],
      season_avoid: ["november", "december", "january", "february"],
      scale_fit: "intimate",
      not_for: ["urban-prestige", "coastal-dramatic", "ultra-modern"],
      price_index: "mid",
      cross_sell: ["provence", "loire-valley", "paris"],
    },
    relatedRegionSlugs: ["provence", "loire-valley"],
  },
];

// --- France cities ---------------------------------------------------------

const FRANCE_CITIES = [
  // Provence
  { slug: "gordes", name: "Gordes", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "Gordes is Provence's most iconic hilltop village, a cluster of ochre stone perched above the Luberon, surrounded by lavender fields and dry-stone terraces." },
  { slug: "luberon", name: "Luberon", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "The Luberon plateau offers some of Provence's most atmospheric estate venues, from restored bergeries to grand bastides overlooking vineyards and lavender." },
  { slug: "aix-en-provence", name: "Aix-en-Provence", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "Aix-en-Provence combines elegant 17th-century hôtels particuliers with café-lined cours for sophisticated city celebrations." },
  { slug: "avignon", name: "Avignon", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "Avignon's papal history lends grandeur to its wedding scene, with venues ranging from restored abbeys to private villas overlooking the Rhône." },
  { slug: "les-baux-de-provence", name: "Les Baux-de-Provence", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "Les Baux-de-Provence is a dramatic citadel village with breathtaking views across the Alpilles, surrounded by estate venues of exceptional quality." },
  { slug: "saint-remy-de-provence", name: "Saint-Rémy-de-Provence", regionSlug: "provence", countrySlug: "france", listingCount: 0, introEditorial: "Saint-Rémy is a chic market town at the foot of the Alpilles, ringed by mas and bastide estates ideal for intimate luxury celebrations." },
  // Paris
  { slug: "marais", name: "Le Marais", regionSlug: "paris", countrySlug: "france", listingCount: 0, introEditorial: "Le Marais blends medieval hôtels particuliers with contemporary gallery spaces for stylish Paris celebrations." },
  { slug: "saint-germain", name: "Saint-Germain-des-Prés", regionSlug: "paris", countrySlug: "france", listingCount: 0, introEditorial: "Saint-Germain delivers Left Bank sophistication in intimate salons and restored 18th-century mansion houses." },
  { slug: "montmartre", name: "Montmartre", regionSlug: "paris", countrySlug: "france", listingCount: 0, introEditorial: "Montmartre offers bohemian charm and sweeping city views from its hilltop perch above Paris." },
  { slug: "champs-elysees", name: "Champs-Élysées", regionSlug: "paris", countrySlug: "france", listingCount: 0, introEditorial: "The Champs-Élysées quarter hosts some of Paris's grandest ballrooms and five-star hotel venues." },
  // Loire Valley
  { slug: "tours", name: "Tours", regionSlug: "loire-valley", countrySlug: "france", listingCount: 0, introEditorial: "Tours is the gateway to the Loire Valley's châteaux, with elegant townhouse venues and easy access to surrounding estates." },
  { slug: "amboise", name: "Amboise", regionSlug: "loire-valley", countrySlug: "france", listingCount: 0, introEditorial: "Amboise sits beneath its royal château overlooking the Loire, a romantic setting for celebrations steeped in Renaissance history." },
  { slug: "blois", name: "Blois", regionSlug: "loire-valley", countrySlug: "france", listingCount: 0, introEditorial: "Blois offers access to some of the Loire's finest château estates, including Chambord and Cheverny nearby." },
  { slug: "saumur", name: "Saumur", regionSlug: "loire-valley", countrySlug: "france", listingCount: 0, introEditorial: "Saumur's troglodyte caves, clifftop château and sparkling wine heritage create a uniquely memorable wedding backdrop." },
  // Normandy
  { slug: "honfleur", name: "Honfleur", regionSlug: "normandy", countrySlug: "france", listingCount: 0, introEditorial: "Honfleur is Normandy's most painterly harbour town, a favourite for intimate celebrations in converted manors and half-timbered houses." },
  { slug: "deauville", name: "Deauville", regionSlug: "normandy", countrySlug: "france", listingCount: 0, introEditorial: "Deauville brings Côte Fleurie glamour, grand Belle Époque hotels and private estate venues overlooking the English Channel." },
  { slug: "etretat", name: "Étretat", regionSlug: "normandy", countrySlug: "france", listingCount: 0, introEditorial: "Étretat's iconic clifftop chalk arches provide a dramatic natural backdrop for coastal estate weddings." },
  { slug: "bayeux", name: "Bayeux", regionSlug: "normandy", countrySlug: "france", listingCount: 0, introEditorial: "Bayeux's medieval cathedral quarter and surrounding bocage countryside offer intimate, history-laden venues." },
  // Dordogne
  { slug: "sarlat", name: "Sarlat-la-Canéda", regionSlug: "dordogne", countrySlug: "france", listingCount: 0, introEditorial: "Sarlat's perfectly preserved medieval centre and surrounding valley châteaux make it the Dordogne's premier wedding destination." },
  { slug: "bergerac", name: "Bergerac", regionSlug: "dordogne", countrySlug: "france", listingCount: 0, introEditorial: "Bergerac's wine country estates and rolling vineyard landscapes make it ideal for relaxed multi-day celebrations." },
  { slug: "perigueux", name: "Périgueux", regionSlug: "dordogne", countrySlug: "france", listingCount: 0, introEditorial: "Périgueux offers grand Périgord manor houses and restored abbey venues in the heart of the Dordogne." },
];

// --- UAE regions -----------------------------------------------------------

const UAE_REGIONS = mapCountryRegions(UAE_REGIONS_RAW, UAE_CITIES, {
  countrySlug: "uae",
  countryName: "UAE",
});

// --- Morocco regions -------------------------------------------------------

const MOROCCO_REGIONS = [
  { slug: "marrakech", name: "Marrakech", countrySlug: "morocco", group: "Morocco", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in Marrakech", heroSubtitle: "Palatial riads, desert camps, and Atlas Mountain retreats.", heroImg: null, introEditorial: "Marrakech is North Africa's premier luxury wedding destination, a sensory feast of zellige mosaics, rose gardens, candlelit courtyards, and Saharan starlight.", categoryShortcuts: [], cities: [], localTerm: "Areas", seo: { title: "Luxury Wedding Venues in Marrakech, Morocco | Luxury Wedding Directory", metaDescription: "Discover Marrakech's finest luxury wedding venues, palatial riads, desert celebrations, and Atlas Mountain retreats.", canonicalPath: "/morocco/marrakech" }, ai: { summary: "Marrakech, palatial riads, desert glamping, and exotic luxury.", focusKeywords: ["marrakech wedding venues", "luxury wedding morocco", "riad wedding marrakech"] }, relatedRegionSlugs: [] },
];

// --- South Africa regions --------------------------------------------------

const SOUTH_AFRICA_REGIONS = [
  { slug: "cape-town", name: "Cape Town", countrySlug: "south-africa", group: "South Africa", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in Cape Town", heroSubtitle: "Table Mountain views, coastal cliffs, and world-class winelands.", heroImg: null, introEditorial: "Cape Town is one of the world's most photogenic wedding cities, with Table Mountain as its crown, Atlantic coastline and a constellation of vineyard estates within thirty minutes' drive.", categoryShortcuts: [], cities: [], localTerm: "Areas", seo: { title: "Luxury Wedding Venues in Cape Town | Luxury Wedding Directory", metaDescription: "Discover Cape Town's finest luxury wedding venues and vendors. Mountain views, coastal estates, and world-class winelands.", canonicalPath: "/south-africa/cape-town" }, ai: { summary: "Cape Town, mountain, ocean, and vineyard estate celebrations.", focusKeywords: ["cape town wedding venues", "luxury wedding cape town", "south africa wedding"] }, relatedRegionSlugs: ["winelands"] },
  { slug: "winelands", name: "Winelands", countrySlug: "south-africa", group: "South Africa", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in the Winelands", heroSubtitle: "Vineyard estates and Cape Dutch manor houses in Stellenbosch and Franschhoek.", heroImg: null, introEditorial: "The Cape Winelands, Stellenbosch, Franschhoek, and Paarl, offer South Africa's most elegant estate wedding venues, surrounded by vines, mountains, and world-class dining.", categoryShortcuts: [], cities: ["stellenbosch", "franschhoek"], localTerm: "Towns", seo: { title: "Luxury Wedding Venues in the Cape Winelands | Luxury Wedding Directory", metaDescription: "Discover the Cape Winelands' finest vineyard estate wedding venues, Stellenbosch, Franschhoek, and Paarl.", canonicalPath: "/south-africa/winelands" }, ai: { summary: "Cape Winelands, vineyard estates and Cape Dutch manor houses.", focusKeywords: ["winelands wedding venues", "stellenbosch wedding", "franschhoek wedding"] }, relatedRegionSlugs: ["cape-town"] },
  { slug: "kruger", name: "Greater Kruger", countrySlug: "south-africa", group: "South Africa", priorityLevel: 2, listingCount: 0, heroTitle: "Safari Weddings", heroSubtitle: "Wild luxury, intimate lodge celebrations surrounded by African wildlife.", heroImg: null, introEditorial: "Greater Kruger's private game reserves offer the world's most dramatic wedding backdrop, sundowner ceremonies, bush dinners under ancient marula trees, and sunrise game drives.", categoryShortcuts: [], cities: [], localTerm: "Reserves", seo: { title: "Safari Wedding Venues in South Africa | Luxury Wedding Directory", metaDescription: "Discover luxury safari lodge wedding venues in Greater Kruger, South Africa.", canonicalPath: "/south-africa/kruger" }, ai: { summary: "Greater Kruger, safari lodge weddings in the African bush.", focusKeywords: ["safari wedding south africa", "kruger wedding venue", "bush wedding lodge"] }, relatedRegionSlugs: ["cape-town", "winelands"] },
];

// --- Sweden regions --------------------------------------------------------

const SWEDEN_REGIONS = [
  { slug: "stockholm", name: "Stockholm", countrySlug: "sweden", group: "Sweden", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in Stockholm", heroSubtitle: "Scandinavian elegance on the Baltic archipelago.", heroImg: null, introEditorial: "Stockholm spans fourteen islands linked by bridges and ferries, offering castle ballrooms, waterfront pavilions, converted industrial spaces, and midnight-sun archipelago celebrations.", categoryShortcuts: [], cities: [], localTerm: "Islands", seo: { title: "Luxury Wedding Venues in Stockholm, Sweden | Luxury Wedding Directory", metaDescription: "Discover Stockholm's finest luxury wedding venues, archipelago islands, castles, and Scandinavian design.", canonicalPath: "/sweden/stockholm" }, ai: { summary: "Stockholm, Scandinavian design, archipelago celebrations, and midnight sun.", focusKeywords: ["stockholm wedding venues", "luxury wedding sweden", "scandinavian wedding"] }, relatedRegionSlugs: [] },
];

// --- Canada regions --------------------------------------------------------

const CANADA_REGIONS = [
  { slug: "british-columbia", name: "British Columbia", countrySlug: "canada", group: "Canada", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in British Columbia", heroSubtitle: "Pacific coastline, mountain peaks, and west coast elegance.", heroImg: null, introEditorial: "British Columbia delivers Canada's most dramatic wedding landscapes, Vancouver's skyline meets ocean, Whistler's alpine grandeur, and the Okanagan's vineyard terraces.", categoryShortcuts: [], cities: ["vancouver", "whistler"], localTerm: "Cities", seo: { title: "Luxury Wedding Venues in British Columbia | Luxury Wedding Directory", metaDescription: "Discover British Columbia's finest luxury wedding venues, Vancouver, Whistler, and Pacific Northwest elegance.", canonicalPath: "/canada/british-columbia" }, ai: { summary: "British Columbia, mountains, Pacific coast, and west coast vineyard celebrations.", focusKeywords: ["vancouver wedding venues", "whistler wedding", "bc wedding venue"] }, relatedRegionSlugs: ["ontario"] },
  { slug: "ontario", name: "Ontario", countrySlug: "canada", group: "Canada", priorityLevel: 1, listingCount: 0, heroTitle: "Weddings in Ontario", heroSubtitle: "Toronto elegance and Niagara wine country.", heroImg: null, introEditorial: "Ontario spans from Toronto's urban sophistication, heritage lofts, rooftop terraces, and gallery spaces, to Niagara-on-the-Lake's vineyard estates and Muskoka's lakeside retreats.", categoryShortcuts: [], cities: ["toronto", "niagara-on-the-lake"], localTerm: "Cities", seo: { title: "Luxury Wedding Venues in Ontario, Canada | Luxury Wedding Directory", metaDescription: "Discover Ontario's finest luxury wedding venues, Toronto, Niagara wine country, and Muskoka.", canonicalPath: "/canada/ontario" }, ai: { summary: "Ontario, Toronto urban elegance and Niagara wine country estates.", focusKeywords: ["toronto wedding venues", "niagara wedding", "ontario wedding venue"] }, relatedRegionSlugs: ["british-columbia"] },
];

// --- Romania regions -------------------------------------------------------

const ROMANIA_REGIONS = [
  { slug: "bucharest-and-ilfov", name: "Bucharest and Ilfov", countrySlug: "romania", group: "Romania", priorityLevel: 1, listingCount: 1, heroTitle: "Weddings in Bucharest", heroSubtitle: "Romania's capital, grand boulevards, Belle Époque palaces, and intimate orangeries.", heroImg: null, introEditorial: "Bucharest blends Parisian-influenced architecture with Eastern European elegance. Its ornate palace venues, private garden estates, and intimate orangeries offer a distinctive backdrop for luxury weddings.", categoryShortcuts: [], cities: ["bucharest"], localTerm: "Cities", seo: { title: "Luxury Wedding Venues in Bucharest & Ilfov | Luxury Wedding Directory", metaDescription: "Discover curated luxury wedding venues in Bucharest and Ilfov, palace gardens, orangeries, and Belle Époque estates.", canonicalPath: "/romania/bucharest-and-ilfov" }, ai: { summary: "Bucharest, Belle Époque palaces, private gardens, and intimate orangeries in Romania's capital.", focusKeywords: ["bucharest wedding venue", "romania wedding", "bucharest orangerie wedding"] }, relatedRegionSlugs: [] },
];

// --- Assemble final REGIONS array -----------------------------------------

import { SUPPLEMENTAL_REGIONS } from "./supplementalRegions.js";

export const REGIONS = [
  ALL_REGIONS_SENTINEL,
  ...ITALY_REGIONS_MAPPED,
  ...UK_IRELAND_FULL_REGIONS,
  ...UK_IRELAND_SKELETON_REGIONS,
  ...FRANCE_REGIONS,
  ...UAE_REGIONS,
  ...MOROCCO_REGIONS,
  ...SOUTH_AFRICA_REGIONS,
  ...SWEDEN_REGIONS,
  ...CANADA_REGIONS,
  ...ROMANIA_REGIONS,
  ...SUPPLEMENTAL_REGIONS,
];

// ---------------------------------------------------------------------------
// CITIES
// ---------------------------------------------------------------------------

// --- Italy cities normalized to unified schema ----------------------------

const ITALY_CITIES_MAPPED = ITALY_CITIES.map(c => ({
  slug: c.slug,
  name: c.name,
  regionSlug: c.regionSlug,
  countrySlug: "italy",
  listingCount: c.listingCount,
  introEditorial: c.description,
}));

// --- UK / Ireland cities (full-content counties only) ---------------------

const UK_IRELAND_CITIES = [
  // London
  {
    slug: "mayfair",
    name: "Mayfair",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Mayfair is the pinnacle of London luxury, home to grand Georgian townhouses and five-star hotel ballrooms.",
  },
  {
    slug: "chelsea",
    name: "Chelsea",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Chelsea offers elegant garden squares and chic venues in one of London's most fashionable neighbourhoods.",
  },
  {
    slug: "kensington",
    name: "Kensington",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Kensington delivers palace-adjacent grandeur and museum-quarter sophistication in the heart of west London.",
  },
  {
    slug: "the-city",
    name: "The City",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "The City of London offers centuries-old livery halls, hidden Jacobean chambers and rooftop terraces above the Square Mile.",
  },
  {
    slug: "hampstead",
    name: "Hampstead",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Hampstead combines village charm with sweeping Heath views for romantic north London celebrations.",
  },
  {
    slug: "shoreditch",
    name: "Shoreditch",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Shoreditch delivers creative warehouse spaces and contemporary style in east London's cultural heart.",
  },
  {
    slug: "richmond",
    name: "Richmond",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Richmond offers riverside venues and royal parkland settings on the elegant southwestern edge of London.",
  },
  {
    slug: "notting-hill",
    name: "Notting Hill",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Notting Hill pairs pastel-coloured townhouses and private garden squares with an effortlessly chic west London atmosphere.",
  },
  {
    slug: "south-bank",
    name: "South Bank",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "The South Bank delivers dramatic Thames-side terraces and contemporary gallery spaces with iconic skyline views.",
  },
  {
    slug: "belgravia",
    name: "Belgravia",
    regionSlug: "london",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Belgravia is London's most refined neighbourhood, stucco-fronted crescents, embassy-grade townhouses, and discreet elegance at every turn.",
  },

  // Surrey
  {
    slug: "guildford",
    name: "Guildford",
    regionSlug: "surrey",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Guildford is Surrey's historic county town, offering castle grounds and riverside venues amid rolling countryside.",
  },
  {
    slug: "farnham",
    name: "Farnham",
    regionSlug: "surrey",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Farnham is a charming Georgian market town with elegant country house venues in the Surrey Hills.",
  },
  {
    slug: "dorking",
    name: "Dorking",
    regionSlug: "surrey",
    countrySlug: "england",
    listingCount: 0,
    introEditorial: "Dorking sits at the heart of the Surrey Hills, surrounded by vineyard estates and Box Hill views.",
  },

  // Edinburgh
  {
    slug: "old-town",
    name: "Old Town",
    regionSlug: "edinburgh",
    countrySlug: "scotland",
    listingCount: 0,
    introEditorial: "Edinburgh's Old Town offers medieval castle venues and historic closes beneath the Castle Rock.",
  },
  {
    slug: "new-town",
    name: "New Town",
    regionSlug: "edinburgh",
    countrySlug: "scotland",
    listingCount: 0,
    introEditorial: "The Georgian New Town provides elegant assembly rooms and sweeping crescent settings for refined celebrations.",
  },
  {
    slug: "leith",
    name: "Leith",
    regionSlug: "edinburgh",
    countrySlug: "scotland",
    listingCount: 0,
    introEditorial: "Leith is Edinburgh's vibrant waterfront district, home to converted warehouses and Michelin-starred dining.",
  },

  // Cardiff
  {
    slug: "cardiff-bay",
    name: "Cardiff Bay",
    regionSlug: "cardiff",
    countrySlug: "wales",
    listingCount: 0,
    introEditorial: "Cardiff Bay offers sleek waterfront venues with views across the barrage and the Senedd.",
  },
  {
    slug: "vale-of-glamorgan",
    name: "Vale of Glamorgan",
    regionSlug: "cardiff",
    countrySlug: "wales",
    listingCount: 0,
    introEditorial: "The Vale of Glamorgan provides country house estates and Heritage Coast settings just minutes from Cardiff.",
  },

  // Dublin
  {
    slug: "city-centre",
    name: "City Centre",
    regionSlug: "dublin",
    countrySlug: "ireland",
    listingCount: 0,
    introEditorial: "Dublin's City Centre delivers Georgian townhouse venues and grand hotel ballrooms around St Stephen's Green.",
  },
  {
    slug: "dalkey",
    name: "Dalkey",
    regionSlug: "dublin",
    countrySlug: "ireland",
    listingCount: 0,
    introEditorial: "Dalkey is a picturesque coastal village south of Dublin with castle venues and harbour views.",
  },
  {
    slug: "howth",
    name: "Howth",
    regionSlug: "dublin",
    countrySlug: "ireland",
    listingCount: 0,
    introEditorial: "Howth is a charming fishing village on Dublin's north side offering clifftop and harbour celebrations.",
  },
];

import { SUPPLEMENTAL_CITIES } from "./supplementalCities.js";

export const CITIES = [
  ...ITALY_CITIES_MAPPED,
  ...UK_IRELAND_CITIES,
  ...FRANCE_CITIES,
  ...SUPPLEMENTAL_CITIES,
];

// ---------------------------------------------------------------------------
// LOOKUP HELPERS
// ---------------------------------------------------------------------------

export function getCountryBySlug(slug) {
  // geo.js inlines only a subset of countries; fall back to the canonical
  // COUNTRY_REGISTRY (name + iso2 for every supported country) so pages like
  // /austria/... resolve a real country instead of null (thin breadcrumb + "Global").
  return COUNTRIES.find(c => c.slug === slug)
      || COUNTRY_REGISTRY.find(c => c.slug === slug)
      || null;
}

export function getRegionBySlug(slug) {
  if (!slug || slug === "all") return REGIONS[0];
  // Fall back to DIRECTORY_REGIONS for regions geo.js does not inline (name +
  // slug + countrySlug), before the ALL sentinel, so region-level pages keep
  // their breadcrumb depth and correct geography.
  return REGIONS.find(r => r.slug === slug)
      || DIRECTORY_REGIONS.find(r => r.slug === slug)
      || REGIONS[0];
}

export function getCityBySlug(slug) {
  return CITIES.find(c => c.slug === slug) || null;
}

export function getRegionsByCountry(countrySlug) {
  return REGIONS.filter(r => r.countrySlug === countrySlug);
}

// Backward-compat helpers
export function getRegionNameBySlug(slug) {
  return getRegionBySlug(slug).name;
}

export function getRegionSlugByName(name) {
  const found = REGIONS.find(r => r.name === name);
  return found ? found.slug : "all";
}

// Strict country derivation map -- no inference
const GROUP_TO_COUNTRY = {
  "Italy": "italy",
  "England": "england",
  "Scotland": "scotland",
  "Wales": "wales",
  "Northern Ireland": "northern-ireland",
  "Channel Islands": "england",
  "Ireland": "ireland",
};

export function countrySlugFromGroup(group) {
  return GROUP_TO_COUNTRY[group] || null;
}

// ---------------------------------------------------------------------------
// REGIONS_FLAT -- backward compatibility
// ---------------------------------------------------------------------------
// Exports region NAMES (labels), not slugs, for backward compat with
// existing filter comparisons in CategoryPage.
// Shape: { name, slug, group }

export const REGIONS_FLAT = REGIONS.map(r => ({
  name: r.name,
  slug: r.slug,
  group: r.group,
}));

// ---------------------------------------------------------------------------
// CANONICAL PATH BUILDERS, for future URL routing, used in SEO panels now
// ---------------------------------------------------------------------------

export function getRegionPath(countrySlug, regionSlug) {
  return `/${countrySlug}/${regionSlug}`;
}

export function getRegionCategoryPath(countrySlug, regionSlug, categorySlug) {
  // Build path based on which slugs are present
  // /{categorySlug}
  // /{countrySlug}/{categorySlug}
  // /{countrySlug}/{regionSlug}/{categorySlug}
  if (countrySlug && regionSlug) {
    return `/${countrySlug}/${regionSlug}/${categorySlug}`;
  } else if (countrySlug) {
    return `/${countrySlug}/${categorySlug}`;
  } else {
    return `/${categorySlug}`;
  }
}

// ---------------------------------------------------------------------------
// VENDOR CATEGORY MAPPING, derived from the category registry
// slug → listingTypes[] used to filter DB rows in RegionCategoryPage
// ---------------------------------------------------------------------------

export function geoSlugToVendorCategory(geoSlug) {
  const types = SLUG_TO_LISTING_TYPES[geoSlug];
  return (types && types.length > 0) ? types : null;
}

export function getVendorCategoryByGeoSlug(geoSlug) {
  return VENDOR_CATEGORIES.find(vc => vc.slug === geoSlug) || null;
}

// ---------------------------------------------------------------------------
// REGION × CATEGORY EDITORIALS
// ---------------------------------------------------------------------------
// Full editorials for London × all 7 categories (showcase county).
// All other combos use template via getRegionCategoryEditorial().

const REGION_CATEGORY_EDITORIALS = {
  "london--wedding-venues":
    "London offers an extraordinary range of luxury wedding venues unlike anywhere else in the United Kingdom. From the timeless grandeur of Mayfair townhouses and Belgravia ballrooms to sweeping Thames-side terraces with skyline views and contemporary gallery spaces in Shoreditch, the capital delivers every style of celebration imaginable. Whether you envision a black-tie affair beneath crystal chandeliers, a bohemian garden party in Hampstead or a dramatic rooftop ceremony overlooking St Paul's Cathedral, London's diversity is its greatest strength. The city's concentration of five-star hotels, private members' clubs, historic livery halls and converted industrial spaces means couples are never short of options at any scale or budget. Our editorial team personally visits every venue before it appears on LWD, ensuring the collection represents only the very finest London has to offer.",

  "london--wedding-planners":
    "London is home to the United Kingdom's highest concentration of luxury wedding planners, from full-service design studios orchestrating multi-day celebrations to specialist day-of coordinators who ensure flawless execution. The capital's planners draw on unrivalled vendor networks spanning florists, caterers, couturiers, lighting designers and entertainment agencies, many of whom work exclusively by referral. Whether you need a bilingual planner for an international guest list, a specialist in Jewish or South Asian ceremonies, or a creative director who can transform a blank-canvas warehouse into a botanical wonderland, London delivers at the highest level. Many of the UK's most awarded planners are based here, bringing editorial-quality design thinking to every celebration. Our editorial team personally vets each planner's portfolio, client references and operational standards before recommending them on LWD.",

  "london--photographers":
    "London's wedding photography scene is among the most sophisticated in the world, shaped by the city's thriving fashion, editorial and documentary photography cultures. The capital's photographers range from fine-art portraitists who create gallery-worthy images to photojournalists who capture raw, unposed emotion with cinematic precision. London itself provides an extraordinary canvas, from the grandeur of Kensington Palace Gardens and the moody elegance of Marylebone to the industrial textures of Bermondsey and the sweeping river views from Battersea Power Station. Many London photographers are published in Vogue, Harpers Bazaar and Tatler, bringing fashion-world sensibility to every wedding they shoot. Whether your style is classic and timeless or bold and contemporary, LWD's curated selection represents only photographers whose work consistently meets the highest editorial standards.",

  "london--florists":
    "London's wedding florists draw on a heritage that stretches from the historic flower markets of Columbia Road and New Covent Garden to the cutting-edge installations of the Chelsea Flower Show. The city's floral designers range from romantic English garden specialists working with seasonal British blooms to avant-garde artists creating suspended installations, living walls and architectural centrepieces that transform entire venues. London florists have access to the widest range of flowers in the UK, sourced from Dutch auctions, British growers and specialist importers, enabling designs that simply aren't possible elsewhere. Whether you envision cascading peonies in soft blush tones, dramatic tropical arrangements, or minimalist single-stem tablescapes, London's florists deliver artistry at every scale. Every florist on LWD is selected for design excellence, reliability and the ability to execute flawlessly under pressure.",

  "london--caterers":
    "London's private dining and wedding catering scene is among the finest in the world, fuelled by the city's extraordinary concentration of Michelin-starred chefs, artisan producers and multi-cultural culinary traditions. From elegant silver-service banquets in historic halls to relaxed sharing feasts and street-food style receptions, London caterers bring world-class creativity to every menu. The city's caterers can accommodate virtually any dietary requirement, cultural tradition or cuisine, whether you want a classic British wedding breakfast, a South Asian feast for five hundred, or a seven-course tasting menu designed by a former Michelin-starred chef. Many of London's top caterers work exclusively with luxury weddings and private events, bringing hotel-level service to any venue. LWD's curated caterers are selected for culinary excellence, service standards and the ability to deliver consistently at scale.",

  "london--hair-makeup":
    "London's bridal beauty industry sits at the intersection of fashion, film and luxury, producing hair and makeup artists whose work graces the pages of Vogue, the runways of London Fashion Week and the sets of major film productions. The capital's bridal specialists offer everything from classic English beauty and vintage Hollywood glamour to contemporary editorial looks and South Asian bridal artistry with intricate techniques honed across generations. London's proximity to the UK's leading beauty brands, product houses and training academies means artists here have access to the latest techniques and products before anywhere else. Most offer trial sessions months in advance, working closely with brides to perfect a look that photographs beautifully in any light. Every artist on LWD is selected for technical skill, professionalism and consistently outstanding client reviews.",

  "london--entertainment":
    "London's wedding entertainment scene is unmatched in the United Kingdom, drawing from the city's world-class music, theatre and performance cultures. From string quartets trained at the Royal Academy of Music and jazz ensembles from Soho's legendary clubs to high-energy party bands, internationally touring DJs and bespoke theatrical experiences, London offers entertainment for every taste and every moment of the day. The city's entertainment agencies curate acts ranging from elegant cocktail-hour performers to spectacular late-night sets that keep dance floors packed until the early hours. Many London-based musicians and performers work regularly at the capital's most prestigious venues and private events, bringing polished professionalism and genuine star quality. LWD's curated entertainment providers are selected for performance excellence, reliability and the ability to read a room and elevate any celebration.",

  // ── Italy wedding planners ──
  "tuscany--wedding-planners":
    "Tuscany's wedding planning studios represent the pinnacle of Italian wedding expertise, drawing on centuries of cultural sophistication and unrivalled vendor relationships throughout the region. From Florence's design-forward planners orchestrating Renaissance villa celebrations to Siena-based specialists in intimate countryside weddings, Tuscany's planners excel at transforming rolling vineyard landscapes into intimate celebrations of extraordinary beauty. These planners navigate the complexities of international destination weddings with effortless grace, from Italian legal requirements and bilingual coordination to sourcing the region's finest wines and connecting with centuries-old artisan families. Many work exclusively through referral, bringing editorial-quality design sensibility to every detail from hand-calligraphed invitations to bespoke menus featuring Chianti wines and black truffle tastings. LWD's curated Tuscan planners are selected for their deep regional knowledge, creative vision, and consistent excellence in orchestrating celebrations of international calibre.",

  "sicily--wedding-planners":
    "Sicily's wedding planners blend Mediterranean luxury with the island's rich cultural heritage, creating celebrations that honour ancient traditions while delivering contemporary sophistication. From Palermo-based studios orchestrating opulent palazzo celebrations to Ragusa specialists in baroque town weddings and Mondello beach ceremonies, Sicily's planners excel at weaving the island's story into every celebration. These planners possess unparalleled knowledge of Sicily's most exclusive private estates, historic convents offering ceremony and reception spaces, and centuries-old artisan families, from embroiderers creating bespoke linens to pasta makers crafting custom wedding menus. Many speak multiple languages and work regularly with international couples, bringing both Italian warmth and professional precision to complex multi-day affairs. LWD's curated Sicilian planners are selected for their deep island knowledge, cultural sensitivity, creative vision, and the ability to deliver flawlessly at every scale.",

  "veneto--wedding-planners":
    "Venice and the Veneto region's wedding planners specialise in creating enchanting celebrations across one of Italy's most romantic and architecturally extraordinary landscapes. From Venetian planners orchestrating gondola processions and private palace ceremonies to Veneto-based specialists in Villa d'Este celebrations and Prosecco region vineyard weddings, these planners excel at translating dream visions into reality across water, countryside and historic towns. The region's planners possess insider access to Venice's most prestigious palaces, the Veneto's most exclusive private estates, and relationships with artisans creating everything from hand-blown Murano glass favours to custom Burano lace details. Many are bilingual or trilingual and navigate Venice's unique logistical requirements with practised ease, coordinating water-based transport, managing strict venue curfews, and producing flawless ceremonies in one of the world's most challenging but rewarding locations. LWD's curated Venetian and Veneto planners are selected for their creative excellence, logistical mastery, and deep cultural knowledge.",

  // ── Amalfi Coast ──
  "amalfi-coast--wedding-venues":
    "Few places on earth carry the weight of romance that the Amalfi Coast does. Cliffside villages tumbling toward turquoise water, lemon groves scenting the morning air, and a quality of light that photographers have chased for centuries. To marry here is to choose a setting that needs no decoration; the landscape does the work. Venues perch on terraces hundreds of metres above the sea in Ravello, cascade through walled gardens in Positano, and open onto private beaches in Praiano, each one carrying a sense of place entirely its own. Celebrations here tend to feel unhurried and deeply personal. Intimate ceremonies where the only backdrop is the Mediterranean horizon, receptions that flow from candlelit terraces into the warm evening air. Our editorial team selects only the venues that genuinely earn their place on the coast. Properties with history, character, and the kind of atmosphere that stays with you long after the day is over.",
};

// ---------------------------------------------------------------------------
// REGION × CATEGORY HERO SUBTITLES, unique, max 160 chars, distinct from editorial
// ---------------------------------------------------------------------------
const REGION_CATEGORY_SUBTITLES = {
  "amalfi-coast--wedding-venues":   "Clifftop estates, walled gardens, private terraces above the sea. The Amalfi Coast is one of the world's great settings for a destination wedding.",
  "london--wedding-venues":         "From Mayfair townhouses to Thames-side terraces and historic livery halls. London delivers every style of luxury wedding imaginable.",
  // ── Global category subtitles (no region) ─────────────────────────────────
  // Key = categorySlug only. Shows on /wedding-vendors, /photographers etc.
  // Edit the text here to change what appears below the heading on those pages.
  "wedding-venues":    "Discover extraordinary venues curated for discerning couples, where every setting is defined by beauty, privacy, and an elevated sense of celebration.",
  "wedding-vendors":   "Each creative within this collection is selected for their vision, reputation, and ability to shape unforgettable celebrations.",
  "wedding-planners":  "Our planners are selected for creative vision, organisational excellence, and genuine expertise in their destination.",
  "photographers":     "Documentary, fine art, editorial. Every photographer here is chosen for a body of work, not a single image.",
  "florists":          "From wild botanical installations to refined centrepieces, our florists are selected for artistry and attention to detail.",
  "bridal-boutiques":  "Curated bridal boutiques offering exclusive designer collections and a truly personal fitting experience.",

  // ── Region + category subtitles ─────────────────────────────────────────────
  "london--wedding-planners":       "The UK's finest wedding planners, with unrivalled vendor networks and the creative vision to bring any celebration to life.",
  "london--photographers":          "London's wedding photographers draw from fashion, editorial and documentary traditions. The result is imagery that lasts a lifetime.",
  "tuscany--wedding-planners":      "Tuscany's planning studios blend Italian craftsmanship with deep regional knowledge, turning rolling vineyard landscapes into unforgettable celebrations.",
  "sicily--wedding-planners":       "Sicily's planners weave Mediterranean warmth with baroque grandeur, creating celebrations shaped by one of Europe's most extraordinary islands.",
  "veneto--wedding-planners":       "From Venetian palaces to Prosecco-region estates, the Veneto's planners navigate one of Italy's most romantic and logistically complex landscapes.",
};

export function getRegionCategorySubtitle(regionSlug, categorySlug) {
  const key = `${regionSlug}--${categorySlug}`;
  if (REGION_CATEGORY_SUBTITLES[key]) return REGION_CATEGORY_SUBTITLES[key];
  // Fall back to category-only key for global pages (no region in URL)
  if (REGION_CATEGORY_SUBTITLES[categorySlug]) return REGION_CATEGORY_SUBTITLES[categorySlug];
  if (!regionSlug) return null;

  const region = REGIONS.find(r => r.slug === regionSlug);
  const vc = VENDOR_CATEGORIES.find(c => c.slug === categorySlug);
  const regionName = region ? region.name : regionSlug
    ? regionSlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : null;
  const label = vc ? vc.label.toLowerCase() : categorySlug.replace(/-/g, " ");
  const place = regionName || "this destination";
  return `Personally curated ${label} in ${place}. Every recommendation is editorially verified, chosen for atmosphere, character, and the experience they create.`;
}

export function getRegionCategoryEditorial(regionSlug, categorySlug) {
  const key = `${regionSlug}--${categorySlug}`;
  if (REGION_CATEGORY_EDITORIALS[key]) return REGION_CATEGORY_EDITORIALS[key];
  if (!regionSlug) return null;

  const region = REGIONS.find(r => r.slug === regionSlug);
  const vc = VENDOR_CATEGORIES.find(c => c.slug === categorySlug);
  const regionName = region ? region.name : regionSlug
    ? regionSlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : null;
  const label = vc ? vc.label.toLowerCase() : categorySlug.replace(/-/g, " ");
  const place = regionName || "This destination";
  return `${place} is one of those rare settings where the landscape does half the work. A place couples return to in their memories long after the celebration is over. The ${label} here carry a sense of place that is entirely their own, shaped by local character, history, and a quality of light that belongs nowhere else. We seek out only the venues and vendors that genuinely earn their place. Properties with atmosphere, integrity, and the kind of hospitality that makes guests feel the day was created specifically for them. Every recommendation in our ${place} collection is personally vetted by our editorial team, chosen on merit, never on spend.`;
}
